import * as React from 'react'
import { GridColumnVisibilityModel, GridDensity } from '@mui/x-data-grid-pro'
import Button from '@mui/material/Button'
import { Duplicate } from '@user/query/useDuplicateCheck'
import { ENTITY_TYPES, isValidEmail } from '@sales-center/common'
import { HighlightedText } from '@sales-center/components'

interface Props {
  onClickManage?: (duplicate: Duplicate) => void
  densityMode?: GridDensity
  highlights?: Record<string, string>
}

export const workWaveContactDuplicatesColumns = ({
  onClickManage,
  densityMode,
  highlights = {},
}: Props) => {
  return [
    {
      width: 120,
      type: 'actions',
      field: 'actions',
      headerName: 'Actions',
      renderHeader: () => null,
      sortable: false,
      resizable: false,
      filterable: false,
      disableReorder: true,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Button
          key={row.key}
          color="primary"
          variant="outlined"
          onClick={() => onClickManage && onClickManage(row)}
          size={densityMode === 'compact' ? 'small' : 'medium'}
          sx={{
            height: densityMode === 'compact' ? '25px' : 'unset',
          }}
          data-test-id={`manage-record-from-duplicate-button-${row.id}`}
        >
          Manage
        </Button>
      ),
    },
    {
      field: 'entityName',
      headerName: 'Primary Contact Name',
      width: 250,
      valueGetter: ({ row }) => row.entityName,
    },
    {
      field: 'firstName',
      headerName: 'Other Contact Name',
      width: 250,
      renderCell: ({ row }) => (
        <HighlightedText
          text={`${row.firstName || ''}${row.lastName?.length > 0 ? ` ${row.lastName}` : ''}`.trim()}
          searchText={`${highlights['firstName'] || ''}${highlights['lastName']?.length > 0 ? ` ${highlights['lastName']}` : ''}`.trim()}
        />
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 350,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['address'] ?? ''}
          text={`${row.address} ${row.city} ${row.state} ${row.postalCode}`.trim()}
        />
      ),
    },
    {
      field: 'company',
      headerName: 'Company Name',
      width: 250,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['company'] ?? ''}
          text={row.company}
        />
      ),
    },
    {
      field: 'emailAddress',
      headerName: 'Email Address',
      width: 300,
      renderCell: ({ row }) =>
        isValidEmail(row.emailAddress) ? (
          <a
            href={`mailto:${row.emailAddress}`}
            target="_blank"
            rel="noreferrer"
            data-test-id={`email-link-from-duplicate-button-${row.id}`}
          >
            <HighlightedText
              searchText={highlights['emailAddress'] ?? ''}
              text={row.emailAddress}
            />
          </a>
        ) : (
          <HighlightedText
            searchText={highlights['emailAddress'] ?? ''}
            text={row.emailAddress}
          />
        ),
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      width: 250,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['phone'] ?? ''}
          text={row.phone}
        />
      ),
    },
    {
      field: 'entityType',
      headerName: 'Record Type',
      width: 250,
      valueGetter: ({ row }) =>
        row.entityType === ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity',
    },
  ]
}

export const workWaveContactDuplicatesDefaultColVis =
  workWaveContactDuplicatesColumns({}).reduce<GridColumnVisibilityModel>(
    (acc, { field }) => {
      if (acc[field] === undefined) {
        return { ...acc, [field]: true }
      } else return acc
    },
    {},
  )

export const workWaveContactDuplicatesDefaultColumns =
  workWaveContactDuplicatesColumns({}).map((c) => c.field)
